import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Briefcase,
  Home,
  MessageSquare,
  Terminal,
  User,
  Search,
  Moon,
  Sun,
  Menu,
  Info,
  BarChart3,
} from "lucide-react";
import { NotificationsPopover } from "./NotificationsPopover";
import { GlobalSearch } from "./GlobalSearch";
import logo from "@/assets/aihr-logo.png";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";
import { useEngineeringInsightsStatus } from "@/lib/engineering-insights/api";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "Home", icon: Home },
  { to: "/workspace", label: "Workspace", icon: MessageSquare },
  { to: "/advisory", label: "Advisory", icon: Briefcase },
  { to: "/console", label: "API & MCP", icon: Terminal },
  { to: "/about", label: "About", icon: Info },
] as const;

const engineeringInsightsNavItem = {
  to: "/dashboards/engineering-insights",
  label: "Engineering Insights",
  icon: BarChart3,
} as const;

export function AppShell({ children }: { children: React.ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const auth = useAihrAuth();
  const engineeringInsights = useEngineeringInsightsStatus();
  // Render the Engineering Insights entry only once the status probe confirms
  // the feature is enabled (nothing while loading or disabled); it slots in
  // right after Workspace.
  const navItems =
    engineeringInsights.data?.enabled === true
      ? [nav[0], nav[1], engineeringInsightsNavItem, ...nav.slice(2)]
      : [...nav];

  const [dark, setDark] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("aihr-theme") : null;
    if (saved === "dark") {
      document.documentElement.classList.add("dark");
      setDark(true);
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("aihr-theme", next ? "dark" : "light");
  };

  const isActive = (to: string) =>
    to === "/"
      ? path === "/"
      : path === to || (to !== "/workspace" && path.startsWith(to));
  const userName = auth.user?.name ?? auth.user?.email ?? "AIHR user";
  const userLabel = auth.authEnabled ? auth.user?.email ?? "Auth0" : "Local header mode";
  const initials = userName
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "AI";

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col bg-[var(--sidebar)] text-[color:var(--sidebar-foreground)]">
        <Link to="/" className="flex items-center gap-2 px-5 py-5">
          <img src={logo} alt="AIHR" className="h-8 w-8 rounded-md" />
          <div>
            <div className="text-[15px] font-semibold tracking-tight text-white">AIHR.Cosmos</div>
            <div className="text-[11px] text-[color:var(--sidebar-muted)]">Internal workspace</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-2 space-y-0.5">
          {navItems.map((n) => {
            const active = isActive(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                className={cn(
                  "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors relative",
                  active
                    ? "bg-white/5 text-white"
                    : "text-[color:var(--sidebar-foreground)] hover:bg-white/5 hover:text-white",
                )}
              >
                {active && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[var(--primary)]" />
                )}
                <Icon className={cn("h-4 w-4", active ? "text-[var(--primary)]" : "text-[color:var(--sidebar-muted)]")} />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-white/5">
          <Link
            to="/profile"
            className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-white/5"
          >
            {auth.user?.picture ? (
              <img
                src={auth.user.picture}
                alt={userName}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[var(--primary)] text-[color:var(--primary-foreground)] grid place-items-center text-xs font-semibold">
                {initials}
              </div>
            )}
            <div className="text-xs">
              <div className="text-white font-medium truncate max-w-[150px]">{userName}</div>
              <div className="text-[color:var(--sidebar-muted)] truncate max-w-[150px]">{userLabel}</div>
            </div>
          </Link>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[var(--sidebar)] text-[color:var(--sidebar-foreground)] p-3" onClick={(e) => e.stopPropagation()}>
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-2 py-3"
            >
              <img src={logo} alt="AIHR" className="h-8 w-8 rounded-md" />
              <span className="text-white font-semibold">AIHR.Cosmos</span>
            </Link>
            <nav className="space-y-0.5">
              {navItems.map((n) => {
                const Icon = n.icon;
                const active = isActive(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm",
                      active ? "bg-white/10 text-white" : "text-[color:var(--sidebar-foreground)]",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 flex items-center gap-3 px-4 md:px-6 border-b border-soft bg-surface">
          <button
            className="md:hidden -ml-1 p-2 rounded-md hover:bg-muted"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5 text-anchor" />
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            className="hidden md:flex items-center gap-2.5 h-10 min-h-10 px-4 rounded-lg border border-soft bg-background text-[13px] text-muted-foreground hover:text-anchor hover:bg-muted/40 transition-colors flex-1 max-w-xl lg:flex-none lg:w-[440px]"
          >
            <Search className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 min-w-0 truncate whitespace-nowrap text-left">
              Search journeys, courses, licenses, members…
            </span>
            <span className="ml-2 shrink-0 text-[11px] font-medium text-muted-foreground border border-soft rounded-md px-2 py-1 bg-surface leading-none">
              ⌘K
            </span>
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            className="md:hidden h-9 w-9 grid place-items-center rounded-md hover:bg-muted text-anchor"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={toggleTheme}
              className="h-9 w-9 grid place-items-center rounded-md hover:bg-muted text-anchor"
              aria-label="Toggle theme"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <NotificationsPopover />
            <Link to="/profile" className="md:hidden h-9 w-9 grid place-items-center rounded-md text-anchor" aria-label="Profile">
              <User className="h-4 w-4" />
            </Link>
          </div>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
